/**
 * Hard-coded description overrides for IFC Index funds.
 * Keyed by numeric investment ID (as returned by the API).
 * When an ID is present here, this HTML description supersedes the API value.
 * Render with [innerHTML]="f.description | safe:'html'"
 */
export const FUND_DESCRIPTION_OVERRIDES: Record<number, string> = {

  // symbol: sce
  884:
    'The IFC US Social Core Equity Index TR is built from three components over time: ' +
    'from January 1928 to September 2005 it reflects the Dimensional Adjusted Market 2 Index; ' +
    'from October 2005 to October 2007 it tracks the DFA US Core Equity 2 Portfolio (DFQTX); ' +
    'and from November 2007 onward it follows the DFA US Social Core Equity 2 Portfolio (DFUEX). ' +
    'The underlying Dimensional US Core Equity 2 Index focuses on U.S. companies listed on major exchanges, ' +
    'emphasizing smaller-cap, lower relative price, and higher profitability firms while excluding non-U.S. companies, ' +
    'REITs, UITs, and investment companies. ' +
    'Performance is shown gross of IFC Advisory Fees. ' +
    'For HYPOTHETICAL back-tested performance data shown, including sources, updates, and important disclosures, see <a href="/disclosures/index-data#SCE">investingforcatholics.com/disclosures</a>.',

  // symbol: re_sr
  24943:
    'The IFC Global REIT Index TR is constructed from multiple components over different time periods: ' +
    'from January 1928 to December 1977 it combines the Dimensional Small Cap Index and Dimensional US Small Cap Value Index; ' +
    'from January 1978 to January 1993 it tracks the Dow Jones US Select REIT Index; ' +
    'from February 1993 to June 2008 it follows the DFA Real Estate Fund (DFREX); ' +
    'from July 2008 to December 2022 it uses the DFA Global Real Estate Fund (DFGEX); ' +
    'and from January 2023 onward it reflects the DFA Global Real Estate ETF (DFGR). ' +
    'The underlying indices and portfolios emphasize diversified exposure to real estate-related securities, ' +
    'including REITs and companies engaged in property development, management, or construction, across U.S. and global markets. ' +
    'The methodology incorporates factors such as market capitalization, relative price, profitability, and asset growth, ' +
    'with historical index data prior to actual fund launches representing back-tested results. ' +
    'Performance is shown gross of IFC Advisory Fees. ' +
    'For HYPOTHETICAL back-tested performance data shown, including sources, updates, and important disclosures, see <a href="/disclosures/index-data#RE_SR">investingforcatholics.com/disclosures</a>.',

  // symbol: ise
  888:
    'The IFC International Social Core Equity Index TR is constructed from multiple components across different time periods: ' +
    'from January 1928 to December 1969 it reflects the Dimensional Adjusted Market 2 Index; ' +
    'from January 1970 to July 1981 it combines the Dimensional UK Market Index and Dimensional International Small Cap Index; ' +
    'from August 1981 to December 1993 it incorporates the DFA Large Cap International Portfolio (DFALX), ' +
    'Dimensional International Small Cap Index, and Dimensional International Small Cap Value Index; ' +
    'from January 1994 to September 2005 it tracks the DFA International Adjusted Market Index; ' +
    'from October 2005 to November 2012 it follows the DFA International Core Equity Portfolio (DFIEX); ' +
    'and from December 2012 onward it uses the DFA International Social Core Equity Portfolio (DSCLX). ' +
    'The underlying indices and portfolios emphasize diversified exposure to non-U.S. developed market equities, ' +
    'with a focus on smaller-cap, lower relative price, and higher profitability companies, ' +
    'while applying social issue screens in more recent periods. ' +
    'Historical data prior to fund inception represent back-tested, hypothetical results assuming reinvested dividends and capital gains. ' +
    'Performance is shown gross of IFC Advisory Fees. ' +
    'For HYPOTHETICAL back-tested performance data shown, including sources, updates, and important disclosures, see <a href="/disclosures/index-data#ISE">investingforcatholics.com/disclosures</a>.',

  // symbol: ems
  534:
    'The IFC Emerging Markets Social Core Index TR is constructed from several components over distinct time periods: ' +
    'from January 1928 to December 1969 it blends the Dimensional Large Value Index, Dimensional US Small Cap Index, ' +
    'and Dimensional US Small Cap Value Index; ' +
    'from January 1970 to December 1974 it combines the Dimensional UK Large Value Index and Dimensional International Small Cap Index; ' +
    'from January 1975 to December 1988 it uses the Fama/French International Value Index and Dimensional International Small Cap Index; ' +
    'from January 1989 to April 1994 it integrates the Fama/French Emerging Markets Index, Dimensional Emerging Value Index, ' +
    'and Fama/French Emerging Markets Small Index; ' +
    'from May 1994 to April 2005 it transitions through combinations of the DFA Emerging Markets Fund (DFEMX), ' +
    'DFA Emerging Markets Value Fund (DFEVX), and DFA Emerging Markets Small Fund (DEMSX); ' +
    'from May 2005 to August 2006 it tracks the DFA Core Emerging Markets Portfolio (DFCEX); ' +
    'and from September 2006 onward it follows the DFA Emerging Markets Social Core Equity Portfolio (DFESX). ' +
    'The underlying indices and portfolios emphasize diversified exposure to emerging and frontier market equities, ' +
    'with a focus on smaller-cap, lower relative price, and higher profitability companies, ' +
    'while incorporating social issue screens in the most recent period. ' +
    'Historical index data prior to fund inception represent back-tested, hypothetical results assuming reinvested dividends and capital gains. ' +
    'Performance is shown gross of IFC Advisory Fees. ' +
    'For HYPOTHETICAL back-tested performance data shown, including sources, updates, and important disclosures, see <a href="/disclosures/index-data#EMS">investingforcatholics.com/disclosures</a>.',

  // symbol: sf
  6970:
    'The IFC Social Fixed Income Index TR is composed of several components across defined time periods: ' +
    'from January 1928 to December 1975 it reflects Long-Term Corporate Bonds; ' +
    'from January 1976 to March 2011 it tracks the Bloomberg U.S. Aggregate Bond Index; ' +
    'from April 2011 to April 2016 it follows the DFA Investment Grade Portfolio (DFAPX); ' +
    'and from May 2016 onward it reflects the DFA Social Fixed Income Portfolio (DSFIX). ' +
    'The underlying indices and portfolios represent broad exposure to investment-grade fixed income securities ' +
    'issued by U.S. and non-U.S. corporate and government entities, ' +
    'with the most recent period incorporating social issue screens to exclude certain issuers. ' +
    'Historical performance data prior to fund inception represent back-tested results assuming reinvestment of earnings. ' +
    'Performance is shown gross of IFC Advisor Fees. ' +
    'For HYPOTHETICAL back-tested performance data shown, including sources, updates, and important disclosures, see <a href="/disclosures/index-data#SF">investingforcatholics.com/disclosures</a>.',

};

/**
 * Description overrides keyed by portfolio symbol.
 * Used for locally-defined portfolios that have no numeric API ID (e.g. target-date benchmarks).
 */
const BENCHMARK_DESC = (year: number | 'Income') =>
  `Blended Prospectus Benchmark for Target Retirement ${
    year === 'Income' ? 'Income' : year
  }. Comprising of Russell 3000 Index, MSCI World ex USA Index, S&P Global REIT Index, MSCI EM, and BBgBarc US Agg Bond. Performance is shown gross of IFC Advisor Fees.`;

const DIMENSIONAL_MUTUAL_FUND_PORTFOLIO_DESC = (equity: number): string => {
  const bond = 100 - equity;
  const parts: string[] = [];
  if (equity > 0) {
    parts.push(`DFUEX — US Social Core Equity 2 Portfolio`);
    parts.push(`DSCLX — International Social Core Equity Portfolio`);
    parts.push(`DFESX — Emerging Markets Social Core Equity Portfolio`);
    parts.push(`DFGEX — Global Real Estate Portfolio`);
  }
  if (bond > 0) {
    parts.push(`DSFIX — Social Fixed Income Portfolio`);
  }
  const positionsText = parts.length > 1
    ? parts.slice(0, -1).join(', ') + ', and ' + parts[parts.length - 1]
    : parts[0];
  return (
    `The Dimensional Mutual Fund Portfolio ${equity} is a blended portfolio comprising ${equity}% equity and ${bond}% fixed income. ` +
    `Its underlying funds are ${positionsText}. ` +
    `Performance is shown gross of IFC Advisory Fees.`
  );
};

const DIMENSIONAL_BENCHMARK_PORTFOLIO_DESC = (equity: number): string => {
  const bond = 100 - equity;
  const parts: string[] = [];
  if (equity > 0) {
    parts.push(`Russell 3000 Index`);
    parts.push(`MSCI World ex USA Index (net div.)`);
    parts.push(`MSCI Emerging Markets Index (net div.)`);
    parts.push(`S&amp;P Global REIT Index (net div.)`);
  }
  if (bond > 0) {
    parts.push(`Bloomberg U.S. Aggregate Bond Index`);
  }
  const positionsText = parts.length > 1
    ? parts.slice(0, -1).join(', ') + ', and ' + parts[parts.length - 1]
    : parts[0];
  return (
    `The Dimensional Prospectus Benchmark Portfolio ${equity} is a blended index comprising ${equity}% equity and ${bond}% fixed income. ` +
    `Its underlying positions are ${positionsText}. ` +
    `Performance is shown gross of IFC Advisory Fees.`
  );
};

const DIMENSIONAL_INDEX_PORTFOLIO_DESC = (equity: number): string => {
  const bond   = 100 - equity;
  const parts: string[] = [];
  if (equity > 0) {
    parts.push(`Dimensional US Core Equity 2 Index`);
    parts.push(`Dimensional International Core Equity 2 Index`);
    parts.push(`Dimensional Emerging Markets Core Equity 2 Index`);
    parts.push(`S&amp;P Global REIT Index (gross div.)`);
  }
  if (bond > 0) {
    parts.push(`Bloomberg U.S. Aggregate Bond Index`);
  }
  const positionsText = parts.length > 1
    ? parts.slice(0, -1).join(', ') + ', and ' + parts[parts.length - 1]
    : parts[0];
  return (
    `The Dimensional Index Portfolio ${equity} is a blended index comprising ${equity}% equity and ${bond}% fixed income. ` +
    `Its underlying positions are ${positionsText}. ` +
    `Performance is shown gross of IFC Advisory Fees.`
  );
};

export const FUND_DESCRIPTION_OVERRIDES_BY_SYMBOL: Record<string, string> = {
  // Dimensional Mutual Fund Portfolios (dfamf0 … dfamf100)
  ...Object.fromEntries(
    Array.from({ length: 21 }, (_, k) => k * 5).map(equity => [
      `dfamf${equity}`,
      DIMENSIONAL_MUTUAL_FUND_PORTFOLIO_DESC(equity),
    ])
  ),
  // Dimensional Index Portfolios (dfa_idp_sr_0 … dfa_idp_sr_100)
  ...Object.fromEntries(
    Array.from({ length: 21 }, (_, k) => k * 5).map(equity => [
      `dfa_idp_sr_${equity}`,
      DIMENSIONAL_INDEX_PORTFOLIO_DESC(equity),
    ])
  ),
  // Dimensional Prospectus Benchmark Portfolios (port0srwfbm_ifa … port100srwfbm_ifa)
  ...Object.fromEntries(
    Array.from({ length: 21 }, (_, k) => k * 5).map(equity => [
      `port${equity}srwfbm_ifa`,
      DIMENSIONAL_BENCHMARK_PORTFOLIO_DESC(equity),
    ])
  ),
  bm2080: BENCHMARK_DESC(2080),
  bm2075: BENCHMARK_DESC(2075),
  bm2070: BENCHMARK_DESC(2070),
  bm2065: BENCHMARK_DESC(2065),
  bm2060: BENCHMARK_DESC(2060),
  bm2055: BENCHMARK_DESC(2055),
  bm2050: BENCHMARK_DESC(2050),
  bm2045: BENCHMARK_DESC(2045),
  bm2040: BENCHMARK_DESC(2040),
  bm2035: BENCHMARK_DESC(2035),
  bm2030: BENCHMARK_DESC(2030),
  bm2025: BENCHMARK_DESC(2025),
  bm2020: BENCHMARK_DESC(2020),
  bm1933: BENCHMARK_DESC('Income'),
};

